import {
  type AxiosError,
  AxiosResponseHeaders,
  RawAxiosResponseHeaders,
} from 'axios'

export const ERROR_TYPES = {
  UNAUTHORIZED: 'unauthorized', // 401/419
  FORBIDDEN: 'forbidden', // 403
  NOT_FOUND: 'not_found', // 404
  CONFLICT: 'conflict', // 409
  VALIDATION: 'validation', // 422
  RATE_LIMITED: 'rate_limited', // 429
  UNEXPECTED: 'unexpected', // other 4xx errors
  SERVER: 'server_error', // 500
  NETWORK: 'network_error',
} as const

type UnauthorizedError = {
  type: typeof ERROR_TYPES.UNAUTHORIZED
  status: 401 | 419
  // message: string
  handledNavigation?: boolean
}

type ForbiddenError = {
  type: typeof ERROR_TYPES.FORBIDDEN
  status: 403
  // message: string
  passwordExpired?: boolean
  handledNavigation?: boolean
}

type NotFoundError = {
  type: typeof ERROR_TYPES.NOT_FOUND
  status: 404
  // message: string
}

type ConflictError = {
  type: typeof ERROR_TYPES.CONFLICT
  status: 409
  // message: string
  conflicts: Record<string, string[]>
  handledNavigation?: boolean
}

type ValidationError = {
  type: typeof ERROR_TYPES.VALIDATION
  status: 422
  // message: string
  errors: Record<string, string[]>
  handledNavigation?: boolean
}

type RateLimitError = {
  type: typeof ERROR_TYPES.RATE_LIMITED
  status: 429
  retryAfterMs?: number
}

type UnexpectedError = {
  type: typeof ERROR_TYPES.UNEXPECTED
  status: number
}

type ServerError = {
  type: typeof ERROR_TYPES.SERVER
  status: number
}

type NetworkError = {
  type: typeof ERROR_TYPES.NETWORK
  timedOut: boolean
}

export type ApiErrorDetail =
  | UnauthorizedError
  | ForbiddenError
  | NotFoundError
  | ConflictError
  | ValidationError
  | RateLimitError
  | UnexpectedError
  | ServerError
  | NetworkError

export class ApiError extends Error {
  override readonly name = 'ApiError'
  constructor(
    message: string,
    readonly detail: ApiErrorDetail,
    options?: ErrorOptions,
  ) {
    super(message, options)
  }
}

interface ErrorBody {
  message?: string
  errors?: Record<string, string[]>
  conflicts?: Record<string, string[]>
  code?: string
  meta?: Record<string, unknown>
  password_expired?: boolean
}

const DEFAULT_MESSAGES: Record<ApiErrorDetail['type'], string> = {
  unexpected: 'Something went wrong. Please try again.',
  unauthorized: 'Your session has expired. Please log in again.',
  forbidden: "You don't have permission to do that.",
  not_found: 'The requested resource was not found.',
  conflict: 'This action could not be completed.',
  validation: 'Please check the highlighted fields.',
  rate_limited: 'Too many requests. Please wait a moment and try again.',
  server_error: 'An unexpected error occurred. Please try again later.',
  network_error: 'Network error. Please check your connection and try again.',
}

export function toApiError(err: AxiosError): ApiError {
  const opts: ErrorOptions = { cause: err }
  if (!err.response) {
    const timedOut = err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
    const detail: ApiErrorDetail = {
      type: 'network_error',
      timedOut,
    }
    return new ApiError(
      timedOut
        ? 'The request timed out. Please try again.'
        : DEFAULT_MESSAGES.network_error,
      detail,
      opts,
    )
  }

  const { status, headers, data } = err.response
  // const errHeaders: ErrorHeaders =
  // (parseInt(err.response.headers['retry-after'], 10) || 60) * 1000,
  const body: ErrorBody =
    typeof data === 'object' && data !== null ? (data as ErrorBody) : {}

  const detail = classify(status, body, headers)

  const message =
    status < 500 && body.message
      ? body.message
      : detail.type === 'forbidden'
        ? detail.passwordExpired
          ? 'Your password has expired. Please update your password.'
          : 'You do not have permission to perform this action.'
        : DEFAULT_MESSAGES[detail.type]

  // let passwordExpired = false
  // if (status === 403) {
  //   if (
  //     typeof data === 'object' &&
  //     data !== null &&
  //     'password_expired' in data &&
  //     (data as { password_expired: unknown }).password_expired === true
  //   ) {
  //     passwordExpired = true
  //   }
  // }

  return new ApiError(message, detail, opts)

  // switch (status) {
  //   case 401:
  //     return new ApiError(
  //       'Your session has expired. Please log in again.',
  //       {
  //         type: ERROR_TYPES.UNAUTHORIZED,
  //         status,
  //         message: 'Your session has expired. Please log in again.', // TODO: We need to only show session expired if the user's session actually expired, not for all 401s (such as session check on app load).
  //       },
  //       opts,
  //     )
  // return {
  //   type: ERROR_TYPES.UNAUTHORIZED,
  //   status,
  //   message: 'Your session has expired. Please log in again.', // TODO: We need to only show session expired if the user's session actually expired, not for all 401s (such as session check on app load).
  // }
  // case 403:
  //   return new ApiError(
  //     passwordExpired
  //       ? 'Your password has expired. Please update your password.'
  //       : 'You do not have permission to perform this action.',
  //     {
  //       type: ERROR_TYPES.FORBIDDEN,
  //       status,
  //       message: passwordExpired
  //         ? 'Your password has expired. Please update your password.'
  //         : 'You do not have permission to perform this action.',
  //       passwordExpired,
  //     },
  //     opts,
  //   )
  // return {
  //   type: ERROR_TYPES.FORBIDDEN,
  //   status,
  //   message: passwordExpired
  //     ? 'Your password has expired. Please update your password.'
  //     : 'You do not have permission to perform this action.',
  //   passwordExpired,
  // }
  // case 404:
  //   return new ApiError(
  //     'The requested resource was not found.',
  //     {
  //       type: ERROR_TYPES.NOT_FOUND,
  //       status,
  //       message: 'The requested resource was not found.',
  //     },
  //     opts,
  //   )
  // return {
  //   type: ERROR_TYPES.NOT_FOUND,
  //   status,
  //   message: 'The requested resource was not found.',
  // }
  // case 409:
  //   return new ApiError(
  //     typeof data === 'object' && data !== null && 'message' in data
  //       ? (data as { message: string }).message
  //       : 'Conflict. The requested resource is in a conflicting state.',
  //     {
  //       type: ERROR_TYPES.CONFLICT,
  //       status,
  //       message:
  //         typeof data === 'object' && data !== null && 'message' in data
  //           ? (data as { message: string }).message
  //           : 'Conflict. The requested resource is in a conflicting state.',
  //       conflicts:
  //         typeof data === 'object' && data !== null && 'conflicts' in data
  //           ? (data as { conflicts: Record<string, string[]> }).conflicts
  //           : {},
  //     },
  //     opts,
  //   )
  // return {
  //   type: ERROR_TYPES.CONFLICT,
  //   status,
  //   message:
  //     typeof data === 'object' && data !== null && 'message' in data
  //       ? (data as { message: string }).message
  //       : 'Conflict. The requested resource is in a conflicting state.',
  //   conflicts:
  //     typeof data === 'object' && data !== null && 'conflicts' in data
  //       ? (data as { conflicts: Record<string, string[]> }).conflicts
  //       : {},
  // }
  // case 422:
  //   return new ApiError(
  //     'Validation error. Please check the provided data and try again.',
  //     {
  //       type: ERROR_TYPES.VALIDATION,
  //       status,
  //       message:
  //         'Validation error. Please check the provided data and try again.',
  //       errors:
  //         typeof data === 'object' && data !== null && 'errors' in data
  //           ? (data as { errors: Record<string, string[]> }).errors
  //           : {},
  //     },
  //     opts,
  //   )
  // return {
  //   type: ERROR_TYPES.VALIDATION,
  //   status,
  //   message:
  //     'Validation error. Please check the provided data and try again.',
  //   errors:
  //     typeof data === 'object' && data !== null && 'errors' in data
  //       ? (data as { errors: Record<string, string[]> }).errors
  //       : {},
  // }
  // case 429:
  //   return new ApiError(
  //     'Too many requests. Please try again later.',
  //     {
  //       type: ERROR_TYPES.RATE_LIMITED,
  //       status,
  //       message: 'Too many requests. Please try again later.',
  //       retryAfterMs:
  //         (parseInt(err.response.headers['retry-after'], 10) || 60) * 1000,
  //     },
  //     opts,
  //   )
  // return {
  //   type: ERROR_TYPES.RATE_LIMITED,
  //   status,
  //   message: 'Too many requests. Please try again later.',
  //   retryAfterMs:
  //     (parseInt(err.response.headers['retry-after'], 10) || 60) * 1000,
  // }
  // default:
  //   return new ApiError(
  //     'An unexpected error occurred. Please try again later.',
  //     {
  //       type: ERROR_TYPES.SERVER,
  //       status,
  //       message: 'An unexpected error occurred. Please try again later.',
  //     },
  //     opts,
  //   )
  // return {
  //   type: ERROR_TYPES.SERVER,
  //   status,
  //   message: 'An unexpected error occurred. Please try again later.',
  // }
  // }
}

function classify(
  status: number,
  body: ErrorBody,
  headers: AxiosResponseHeaders | RawAxiosResponseHeaders,
): ApiErrorDetail {
  if (status === 422)
    return { status, type: 'validation', errors: body?.errors ?? {} }

  switch (status) {
    case 401:
    case 419:
      return { status, type: 'unauthorized' }
    case 403:
      return {
        status,
        type: 'forbidden',
        passwordExpired: body.password_expired ?? false,
      }
    case 404:
      return { status, type: 'not_found' }
    case 409:
      return { status, type: 'conflict', conflicts: body.conflicts ?? {} }
    case 429:
      return {
        status,
        type: 'rate_limited',
        retryAfterMs: parseRetryAfter(headers),
      }
  }

  if (status >= 500) return { status, type: 'server_error' }
  return { status, type: 'unexpected' }
}

function parseRetryAfter(
  headers: AxiosResponseHeaders | RawAxiosResponseHeaders,
): number | undefined {
  const raw = headers['retry-after']
  if (raw === null) return undefined
  const seconds = Number(raw)
  if (!Number.isNaN(seconds)) return seconds * 1000
  return undefined
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
  // return (
  //   typeof error === 'object' &&
  //   error !== null &&
  //   'type' in error &&
  //   typeof (error as { type: unknown }).type === 'string' &&
  //   Object.values(ERROR_TYPES).includes(
  //     (error as { type: string })
  //       .type as (typeof ERROR_TYPES)[keyof typeof ERROR_TYPES],
  //   )
  // )
}

export function isRateLimitedError(error: unknown): error is RateLimitError {
  return isApiError(error) && error.detail.type === ERROR_TYPES.RATE_LIMITED
}

/** The API is busy, restarting or unreachable (429, 5xx, network): nothing is known about the session */
export function isTransientApiError(error: unknown): error is ApiError {
  return (
    isApiError(error) &&
    (error.detail.type === ERROR_TYPES.RATE_LIMITED ||
      error.detail.type === ERROR_TYPES.SERVER ||
      error.detail.type === ERROR_TYPES.NETWORK)
  )
}

// export function normalizeApiError(err: unknown): ApiError {
//   if (axios.isAxiosError(err)) {
//     return toApiError(err)
//   }

//   return {
//     type: ERROR_TYPES.SERVER,
//     status: 500,
//     message:
//       err instanceof Error
//         ? err.message
//         : 'An unexpected error occurred. Please try again later.',
//   }
// }
